<template>
  <div id="app">
    <header v-if="isLoggedIn" class="app-header">
      <div class="container header-container">
        <div class="logo">
          <img src="./assets/biomakers-logo.svg" alt="BioPortal Logo" class="logo-image" />
          <h1>BioPortal</h1>
        </div>
        <nav class="nav-menu">
          <router-link to="/dashboard" class="nav-link">
            <i class="pi pi-home"></i> Dashboard
          </router-link>
          <router-link to="/upload" class="nav-link">
            <i class="pi pi-upload"></i> Subir Archivos
          </router-link>
          <router-link to="/browse" class="nav-link">
            <i class="pi pi-folder-open"></i> Explorar Archivos
          </router-link>
          <router-link v-if="isAdmin" to="/users" class="nav-link">
            <i class="pi pi-users"></i> Usuarios
          </router-link>
        </nav>
        <div class="user-info">
          <div class="user-details">
            <span class="user-name">{{ currentUser?.full_name || currentUser?.email }}</span>
            <span class="user-role">{{ currentUser?.is_admin ? 'Administrador' : 'Usuario' }}</span>
          </div>
          <Button icon="pi pi-sign-out" class="p-button-rounded p-button-text" @click="logout" tooltip="Cerrar Sesión" />
        </div>
      </div>
    </header>

    <Toast position="top-right" />
    
    <main class="app-main fade-in">
      <router-view/>
    </main>

    <footer class="app-footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-logo">
            <h3>BioPortal</h3>
            <p>Gestión de Archivos Biológicos</p>
          </div>
          <div class="footer-links">
            <h4>Enlaces</h4>
            <ul>
              <li><a href="#" @click.prevent="goToDashboard">Dashboard</a></li>
              <li><a href="#" @click.prevent="goToUpload">Subir Archivos</a></li>
              <li><a href="#" @click.prevent="goToBrowse">Explorar Archivos</a></li>
              <li v-if="isAdmin"><a href="#" @click.prevent="goToUsers">Administrar Usuarios</a></li>
            </ul>
          </div>
          <div class="footer-contact">
            <h4>Contacto</h4>
            <p><i class="pi pi-envelope"></i> </p>
            <p><i class="pi pi-phone"></i> </p>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; 2025 BioPortal - Biomarkers - Todos los derechos reservados</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  name: 'App',
  computed: {
    ...mapState('auth', ['isLoggedIn', 'currentUser']),
    ...mapGetters('auth', ['isAdmin'])
  },
  methods: {
    ...mapActions('auth', ['logout']),
    goToDashboard() {
      if (this.isLoggedIn) this.$router.push({ name: 'dashboard' })
    },
    goToUpload() {
      if (this.isLoggedIn) this.$router.push({ name: 'upload' })
    },
    goToBrowse() {
      if (this.isLoggedIn) this.$router.push({ name: 'browse' })
    },
    goToUsers() {
      if (this.isLoggedIn && this.isAdmin) this.$router.push({ name: 'users' })
    }
  },
  created() {
    // Verificar si hay un token en localStorage para mantener la sesión
    const token = localStorage.getItem('token')
    if (token) {
      this.$store.dispatch('auth/setToken', token)
    }
  }
}
</script>

<style>
#app {
  font-family: 'Avenir', 'Helvetica', 'Arial', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.app-header {
  background-color: var(--secondary-dark);
  color: white;
  padding: 10px 0;
  box-shadow: 0 2px 5px rgba(22, 16, 48, 0.2);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo-image {
  height: 36px;
  width: auto;
  background-color: #0d026400;
  padding: 5px;
  border-radius: 6px;
}

.logo h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: white;
}

.nav-menu {
  display: flex;
  gap: 20px;
}

.nav-link {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  padding: 6px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;
}

.nav-link:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-link.router-link-active {
  background-color: var(--primary-color);
  color: white;
  font-weight: 500;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.user-details {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.user-name {
  font-weight: 500;
  font-size: 0.9rem;
}

.user-role {
  font-size: 0.75rem;
  opacity: 0.8;
}

.app-main {
  flex: 1;
  padding: 20px 0;
  background-color: var(--background-color);
}

.app-footer {
  background-color: var(--secondary-dark);
  color: white;
  padding: 40px 0 20px;
  margin-top: auto;
}

.footer-content {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 30px;
  margin-bottom: 30px;
}

.footer-logo h3 {
  margin: 0;
  font-size: 1.5rem;
  color: white;
}

.footer-logo p {
  margin: 5px 0 0;
  color: rgba(255, 255, 255, 0.7);
}

.footer-links h4, .footer-contact h4 {
  color: white;
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 1.1rem;
}

.footer-links ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin-bottom: 8px;
}

.footer-links a {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: color 0.2s ease;
}

.footer-links a:hover {
  color: white;
}

.footer-contact p {
  margin: 8px 0;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  gap: 8px;
}

.footer-bottom {
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
}

.footer-bottom p {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
}

@media (max-width: 768px) {
  .header-container {
    flex-direction: column;
    gap: 15px;
    padding: 10px 15px;
  }
  
  .nav-menu {
    width: 100%;
    justify-content: center;
    gap: 10px;
  }
  
  .user-info {
    width: 100%;
    justify-content: center;
  }
  
  .footer-content {
    flex-direction: column;
    gap: 20px;
  }
  
  .footer-logo, .footer-links, .footer-contact {
    width: 100%;
    text-align: center;
  }
}
</style> 